/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    getTelemetryObject,
    Module,
    Node,
    ITelemetryContent,
    IComponentNodeProps,
    ISingleSlideCarouselProps,
    SingleSlideCarousel
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IImageListViewProps } from '@msdyn365-commerce-modules/image-list/src/modules/image-list/./image-list';
import { IImageListProps } from '../definition-extensions/image-list.ext.props.autogenerated';

/**
 * Render the thumbnail item images.
 * @param props - The image list view props.
 * @returns Return HTML having IImageListViewProps props with image List.
 */
export const imageListView = (props: IImageListViewProps & IImageListProps<{}>): JSX.Element => {
    const _telemetryContent: ITelemetryContent = getTelemetryObject(
        props.context.request.telemetryPageName!,
        props.friendlyName,
        props.telemetry
    );
    const { imageList, imageListContainer, imageListHeading, imageListItem, imageListItems, imageListItemAnchorTag } = props;
    const propsCarousel: IComponentNodeProps<ISingleSlideCarouselProps> = {
        tag: SingleSlideCarousel,
        className: props.config.className ?? '',
        flipperPrevLabel: 'Previous',
        flipperNextLabel: 'Next',
        parentId: props.id,
        telemetryContent: _telemetryContent,
        vertical: false
    };

    return (
        <Module {...imageList}>
            {imageListHeading}
            <Node {...imageListContainer}>
                {props.config?.enableCarousel ? (
                    <Node {...propsCarousel}>
                        {imageListItems?.map(item => {
                            let indexKey: string;

                            if (item.image) {
                                const image = (item.image as unknown) as Msdyn365.Image;
                                indexKey = image.props.src as string;
                            } else {
                                const text = (item.text as unknown) as Msdyn365.RichTextComponent;
                                indexKey = text.props.text as string;
                            }
                            return (
                                <Node {...imageListItem} key={indexKey}>
                                    {item.image && (
                                        <Node
                                            {...imageListItemAnchorTag}
                                            href={item.link}
                                            altText={item.linkAltText}
                                            aria-label={item.linkAriaLabel}
                                            className={imageListItemAnchorTag.className}
                                        >
                                            {item.image}
                                            {props.config?.wrapTextWithAnchorTag && item.text}
                                        </Node>
                                    )}
                                    {!props.config?.wrapTextWithAnchorTag && item.text}
                                </Node>
                            );
                        })}
                    </Node>
                ) : (
                    imageListItems?.map(item => {
                        let indexKey: string;

                        if (item.image) {
                            const image = (item.image as unknown) as Msdyn365.Image;
                            indexKey = image.props.src as string;
                        } else {
                            const text = (item.text as unknown) as Msdyn365.RichTextComponent;
                            indexKey = text.props.text as string;
                        }
                        return (
                            <Node {...imageListItem} key={indexKey}>
                                {item.image && (
                                    <Node
                                        {...imageListItemAnchorTag}
                                        href={item.link}
                                        altText={item.linkAltText}
                                        aria-label={item.linkAriaLabel}
                                        className={imageListItemAnchorTag.className}
                                    >
                                        {item.image}
                                        {props.config?.wrapTextWithAnchorTag && item.text}
                                    </Node>
                                )}
                                {!props.config?.wrapTextWithAnchorTag && item.text}
                            </Node>
                        );
                    })
                )}
            </Node>
        </Module>
    );
};

export default imageListView;
